/* ============================================================
   LUSCIOUS LEOPARD — IT SERVICES PAGE INTERACTIONS
   Loaded (defer) by every it-* section; guarded so it runs once.
   ============================================================ */
(function () {
  'use strict';
  if (window.__llitInit) return;
  window.__llitInit = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  document.documentElement.classList.add('llit-js');

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* ---------- masked word reveal (jitter-style headline) ---------- */
  function initWords() {
    if (reduced) return;
    document.querySelectorAll('[data-llit-words]').forEach(function (el) {
      var words = el.textContent.trim().split(/\s+/);
      el.textContent = '';
      el.classList.remove('llit-r');
      words.forEach(function (word, i) {
        var wrap = document.createElement('span');
        wrap.className = 'llit-w-wrap';
        var inner = document.createElement('span');
        inner.className = 'llit-w';
        inner.style.setProperty('--wi', i);
        inner.textContent = word;
        wrap.appendChild(inner);
        el.appendChild(wrap);
        if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      });
    });
  }

  /* ---------- count-up numbers ---------- */
  function initCounters() {
    var els = document.querySelectorAll('[data-llit-count]');
    if (!els.length) return;
    function run(el) {
      var target = parseInt(el.dataset.llitCount, 10) || 0;
      var suffix = el.dataset.llitSuffix || '';
      if (reduced) {
        el.textContent = target + suffix;
        return;
      }
      var t0 = null;
      var dur = 1400;
      function tick(t) {
        if (!t0) t0 = t;
        var p = Math.min((t - t0) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);
        el.textContent = Math.round(target * eased) + suffix;
        if (p < 1) requestAnimationFrame(tick);
      }
      requestAnimationFrame(tick);
    }
    if (!('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.textContent = (el.dataset.llitCount || '') + (el.dataset.llitSuffix || '');
      });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          run(entry.target);
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- scroll reveal ---------- */
  function initReveals() {
    var targets = document.querySelectorAll('[data-llit-reveal], .llit-r');
    if (!('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.1 }
    );
    targets.forEach(function (el) { io.observe(el); });
  }

  /* ---------- generic accessible tabs (services + industries) ----------
     container[data-llit-tabs] holds buttons[data-llit-tab="id"] and
     panels[data-llit-panel="id"]. */
  function initTabs() {
    document.querySelectorAll('[data-llit-tabs]').forEach(function (root) {
      var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-llit-tab]'));
      var panels = Array.prototype.slice.call(root.querySelectorAll('[data-llit-panel]'));
      if (!tabs.length || !panels.length) return;
      root.classList.add(root.dataset.llitTabs + '--tabs');

      function activate(id, focus) {
        tabs.forEach(function (t) {
          var on = t.dataset.llitTab === id;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
          if (on && focus) t.focus();
        });
        panels.forEach(function (p) {
          p.classList.toggle('is-active', p.dataset.llitPanel === id);
        });
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { activate(tab.dataset.llitTab); });
        if (finePointer && root.dataset.llitHover === 'true') {
          tab.addEventListener('mouseenter', function () { activate(tab.dataset.llitTab); });
        }
        tab.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowDown' || e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
          if (e.key === 'Home') next = tabs[0];
          if (e.key === 'End') next = tabs[tabs.length - 1];
          if (next) {
            e.preventDefault();
            activate(next.dataset.llitTab, true);
          }
        });
      });

      activate(tabs[0].dataset.llitTab);
    });
  }

  /* ---------- journey: auto-cycling live step ---------- */
  function initJourneyCycle() {
    if (reduced || !('IntersectionObserver' in window)) return;
    document.querySelectorAll('[data-llit-journey]').forEach(function (root) {
      var steps = Array.prototype.slice.call(root.querySelectorAll('.llit-journey__step'));
      if (!steps.length) return;
      var idx = -1;
      var timer = null;
      var startT = null;
      function tick() {
        idx = (idx + 1) % steps.length;
        steps.forEach(function (s, i) {
          s.classList.toggle('is-live', i === idx);
        });
      }
      function start(delay) {
        if (timer || startT) return;
        startT = setTimeout(function () {
          startT = null;
          root.classList.add('is-fired');
          tick();
          timer = setInterval(tick, 1600);
        }, delay);
      }
      function stop() {
        if (startT) {
          clearTimeout(startT);
          startT = null;
        }
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
        steps.forEach(function (s) {
          s.classList.remove('is-live');
        });
        idx = -1;
      }
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            // wait out the entrance ignition on first view, restart fast after
            start(root.classList.contains('is-fired') ? 300 : 2600);
          } else {
            stop();
          }
        });
      }, { threshold: 0.3 }).observe(root);
    });
  }

  /* ---------- AI console: self-executing step loader ---------- */
  function initAiLoader() {
    document.querySelectorAll('[data-llit-ailoader]').forEach(function (root) {
      var steps = Array.prototype.slice.call(root.querySelectorAll('.llit-ai__step'));
      var conns = Array.prototype.slice.call(root.querySelectorAll('.llit-ai__conn'));
      if (!steps.length) return;
      if (reduced || !('IntersectionObserver' in window)) {
        steps.forEach(function (s) { s.classList.add('is-done'); });
        conns.forEach(function (c) { c.classList.add('is-traced'); });
        return;
      }
      var running = false;
      var tId = null;
      function reset() {
        steps.forEach(function (s) { s.classList.remove('is-active', 'is-done'); });
        conns.forEach(function (c) { c.classList.remove('is-traced'); });
      }
      function seq(i) {
        if (!running) return;
        if (i >= steps.length) {
          // full cycle complete: hold, then restart
          tId = setTimeout(function () {
            reset();
            tId = setTimeout(function () { seq(0); }, 500);
          }, 1800);
          return;
        }
        steps[i].classList.add('is-active');
        tId = setTimeout(function () {
          steps[i].classList.remove('is-active');
          steps[i].classList.add('is-done');
          if (conns[i]) {
            // trace the beam down to the next box before it starts
            conns[i].classList.add('is-traced');
            tId = setTimeout(function () { seq(i + 1); }, 430);
          } else {
            seq(i + 1);
          }
        }, 1150);
      }
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            if (!running) {
              running = true;
              tId = setTimeout(function () { seq(0); }, 400);
            }
          } else {
            running = false;
            clearTimeout(tId);
            reset();
          }
        });
      }, { threshold: 0.35 }).observe(root);
    });
  }

  /* ---------- magnetic CTAs ---------- */
  function initMagnetic() {
    if (reduced || !finePointer) return;
    document.querySelectorAll('[data-llit-magnetic]').forEach(function (el) {
      var strength = 8;
      el.addEventListener('pointermove', function (e) {
        var r = el.getBoundingClientRect();
        var x = ((e.clientX - r.left) / r.width - 0.5) * 2;
        var y = ((e.clientY - r.top) / r.height - 0.5) * 2;
        el.style.transform = 'translate(' + (x * strength).toFixed(1) + 'px,' + (y * strength).toFixed(1) + 'px)';
      });
      el.addEventListener('pointerleave', function () {
        el.style.transform = '';
      });
    });
  }

  /* ---------- hero canvas: circuit-trace system animation ----------
     Orthogonal "PCB trace" paths on a grid. Light pulses travel the
     traces; traces brighten near the cursor. Monochrome, engineered. */
  function initHeroCanvas() {
    var canvas = document.getElementById('llit-hero-canvas');
    if (!canvas || !canvas.getContext) return;
    var ctx = canvas.getContext('2d');
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var traces = [];
    var W = 0;
    var H = 0;
    var CELL = 44;
    var raf = null;
    var pointer = { x: -9999, y: -9999 };

    function buildTrace(cols, rows) {
      var dirs = [[1, 0], [-1, 0], [0, 1], [0, -1]];
      var cx = 1 + Math.floor(Math.random() * (cols - 2));
      var cy = 1 + Math.floor(Math.random() * (rows - 2));
      var pts = [{ x: cx, y: cy }];
      var dir = dirs[Math.floor(Math.random() * 4)];
      var segs = 3 + Math.floor(Math.random() * 4);
      for (var s = 0; s < segs; s++) {
        var len = 1 + Math.floor(Math.random() * 3);
        cx = Math.max(0, Math.min(cols, cx + dir[0] * len));
        cy = Math.max(0, Math.min(rows, cy + dir[1] * len));
        var last = pts[pts.length - 1];
        if (cx !== last.x || cy !== last.y) pts.push({ x: cx, y: cy });
        // turn 90 degrees
        dir = dir[0] !== 0
          ? dirs[2 + Math.floor(Math.random() * 2)]
          : dirs[Math.floor(Math.random() * 2)];
      }
      if (pts.length < 3) return null;
      var abs = pts.map(function (p) {
        return { x: p.x * CELL + 0.5, y: p.y * CELL + 0.5 };
      });
      var total = 0;
      var segLens = [];
      for (var i = 1; i < abs.length; i++) {
        var l = Math.abs(abs[i].x - abs[i - 1].x) + Math.abs(abs[i].y - abs[i - 1].y);
        segLens.push(l);
        total += l;
      }
      if (total < CELL * 2) return null;
      return {
        pts: abs,
        segLens: segLens,
        total: total,
        prog: Math.random(),
        speed: (0.35 + Math.random() * 0.5) * 60 / total, // progress per second
        mid: abs[Math.floor(abs.length / 2)]
      };
    }

    function resize() {
      var rect = canvas.parentElement.getBoundingClientRect();
      W = rect.width;
      H = rect.height;
      canvas.width = W * dpr;
      canvas.height = H * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      var cols = Math.floor(W / CELL);
      var rows = Math.floor(H / CELL);
      var want = Math.min(26, Math.max(10, Math.round(W / 70)));
      traces = [];
      var guard = 0;
      while (traces.length < want && guard < want * 6) {
        guard++;
        var t = buildTrace(cols, rows);
        if (t) traces.push(t);
      }
    }

    function pointAt(t, dist) {
      var d = dist;
      for (var i = 0; i < t.segLens.length; i++) {
        if (d <= t.segLens[i]) {
          var a = t.pts[i];
          var b = t.pts[i + 1];
          var f = t.segLens[i] === 0 ? 0 : d / t.segLens[i];
          return { x: a.x + (b.x - a.x) * f, y: a.y + (b.y - a.y) * f };
        }
        d -= t.segLens[i];
      }
      return t.pts[t.pts.length - 1];
    }

    var lastT = 0;
    function frame(now, step) {
      var dt = step && lastT ? Math.min((now - lastT) / 1000, 0.05) : 0;
      lastT = now;
      ctx.clearRect(0, 0, W, H);
      for (var i = 0; i < traces.length; i++) {
        var t = traces[i];
        // cursor proximity boost
        var pdx = t.mid.x - pointer.x;
        var pdy = t.mid.y - pointer.y;
        var pd = Math.sqrt(pdx * pdx + pdy * pdy);
        var boost = pd < 260 ? (1 - pd / 260) * 0.14 : 0;

        // trace path
        ctx.strokeStyle = 'rgba(255,255,255,' + (0.09 + boost).toFixed(3) + ')';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(t.pts[0].x, t.pts[0].y);
        for (var j = 1; j < t.pts.length; j++) ctx.lineTo(t.pts[j].x, t.pts[j].y);
        ctx.stroke();

        // corner vias
        ctx.fillStyle = 'rgba(255,255,255,' + (0.16 + boost).toFixed(3) + ')';
        for (var k = 1; k < t.pts.length - 1; k++) {
          ctx.beginPath();
          ctx.arc(t.pts[k].x, t.pts[k].y, 1.4, 0, Math.PI * 2);
          ctx.fill();
        }
        // endpoint nodes: hollow start, filled end
        var p0 = t.pts[0];
        var pn = t.pts[t.pts.length - 1];
        ctx.strokeStyle = 'rgba(255,255,255,' + (0.3 + boost).toFixed(3) + ')';
        ctx.beginPath();
        ctx.arc(p0.x, p0.y, 3, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(pn.x, pn.y, 2.2, 0, Math.PI * 2);
        ctx.fill();

        // traveling pulse with short trail
        if (step) t.prog = (t.prog + t.speed * dt) % 1;
        var head = t.prog * t.total;
        var trail = Math.max(0, head - CELL * 0.8);
        var hp = pointAt(t, head);
        var tp = pointAt(t, trail);
        var grad = null;
        try {
          grad = ctx.createLinearGradient(tp.x, tp.y, hp.x, hp.y);
          grad.addColorStop(0, 'rgba(255,255,255,0)');
          grad.addColorStop(1, 'rgba(255,255,255,' + (0.4 + boost * 2).toFixed(3) + ')');
        } catch (e) {
          grad = 'rgba(255,255,255,0.35)';
        }
        // draw trail along path segments between trail..head
        ctx.strokeStyle = grad;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(tp.x, tp.y);
        var acc = 0;
        for (var m = 0; m < t.segLens.length; m++) {
          var segEnd = acc + t.segLens[m];
          if (segEnd > trail && acc < head) {
            var cornerP = t.pts[m + 1];
            if (segEnd <= head) ctx.lineTo(cornerP.x, cornerP.y);
          }
          acc = segEnd;
        }
        ctx.lineTo(hp.x, hp.y);
        ctx.stroke();
        ctx.fillStyle = 'rgba(255,255,255,' + (0.55 + boost * 2).toFixed(3) + ')';
        ctx.beginPath();
        ctx.arc(hp.x, hp.y, 2.4, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function loop(ts) {
      frame(ts || 0, true);
      raf = requestAnimationFrame(loop);
    }

    resize();
    if (reduced) {
      frame(0, false); // single static frame
    } else {
      loop();
      // pause when off-screen
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              if (!raf) loop();
            } else if (raf) {
              cancelAnimationFrame(raf);
              raf = null;
            }
          });
        }).observe(canvas);
      }
      if (finePointer) {
        canvas.parentElement.addEventListener('pointermove', function (e) {
          var r = canvas.getBoundingClientRect();
          pointer.x = e.clientX - r.left;
          pointer.y = e.clientY - r.top;
        });
        canvas.parentElement.addEventListener('pointerleave', function () {
          pointer.x = -9999;
          pointer.y = -9999;
        });
      }
    }
    var rT;
    window.addEventListener('resize', function () {
      clearTimeout(rT);
      rT = setTimeout(function () {
        resize();
        if (reduced) frame(0, false);
      }, 200);
    });
  }

  ready(function () {
    initWords();
    initReveals();
    initTabs();
    initMagnetic();
    initHeroCanvas();
    initCounters();
    initJourneyCycle();
    initAiLoader();
  });
})();
